/* =============================================================================
 *  TerminalCreateInvoiceModal
 *
 *  Terminal-native replacement for `CreateInvoiceModal`. Prop-identical so
 *  hosts swap with a single conditional. Same feature surface:
 *   - Three modes: create · edit (via `editInvoice`) · copy (via `templateInvoice`)
 *   - Customer selector (disabled in edit mode — Xero doesn't allow contact switch)
 *   - Invoice date / due date / reference / account code
 *   - GST treatment (Exclusive / Inclusive / NoTax)
 *   - Status (DRAFT / APPROVED)
 *   - Line items editor with per-row amount calculation
 *   - Live subtotal / GST / total panel
 *   - Async line-item hydration for edit + copy modes (fetchInvoiceDetail)
 *   - Validation + error/success banners
 *   - Submit → createInvoice or updateInvoice via useInvoice() context
 * ============================================================================= */

import React, { useEffect, useRef, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useInvoice } from '../contexts/InvoiceContext';
import { useAppData } from '../../../providers/AppDataProvider';
import { Invoice, LineAmountType } from '../../../types/accounting';
import './TerminalCreateInvoiceModal.scss';

interface LineItemRow {
  id: string;
  description: string;
  quantity: string;
  unitAmount: string;
}

interface TerminalCreateInvoiceModalProps {
  show: boolean;
  onHide: () => void;
  editInvoice?: Invoice | null;
  preSelectedCustomer?: { id: string; name: string; email?: string } | null;
  templateInvoice?: Invoice | null;
}

const isoDay = (d: Date) => d.toISOString().slice(0, 10);
const today = () => isoDay(new Date());
const defaultDueDate = () => isoDay(new Date(Date.now() + 14 * 24 * 60 * 60 * 1000));

const genId = () => Math.random().toString(36).substring(2, 10);
const fmtCurrency = (amount: number) =>
  new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(amount);

export function TerminalCreateInvoiceModal({
  show, onHide, editInvoice, preSelectedCustomer, templateInvoice,
}: TerminalCreateInvoiceModalProps) {
  const {
    createInvoice, createInvoiceLoading,
    updateInvoice, updateInvoiceLoading,
    fetchInvoiceDetail,
  } = useInvoice();
  const { customers } = useAppData();
  const isEditMode = !!editInvoice;
  const isCopyMode = !isEditMode && !!templateInvoice;

  const [selectedCustomer, setSelectedCustomer] =
    useState<{ id: string; name: string; email?: string } | null>(null);
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [dueDate, setDueDate]         = useState(defaultDueDate());
  const [reference, setReference]     = useState('');
  const [accountCode, setAccountCode] = useState('200');
  const [lineAmountTypes, setLineAmountTypes] = useState<LineAmountType>('Exclusive');
  const [status, setStatus]           = useState<'DRAFT' | 'APPROVED'>('APPROVED');
  const [lineItems, setLineItems]     = useState<LineItemRow[]>([
    { id: genId(), description: '', quantity: '1', unitAmount: '' },
  ]);
  const [loadingLineItems, setLoadingLineItems] = useState(false);

  const [error, setError]     = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Only re-initialize state on the transition from closed → open so typing
  // doesn't get wiped by the effect firing on re-renders.
  const prevShowRef = useRef(false);
  useEffect(() => {
    const justOpened = show && !prevShowRef.current;
    prevShowRef.current = show;
    if (!justOpened) return;

    setError(null);
    setSuccess(null);
    setLoadingLineItems(false);

    const populateLineItems = (inv: Invoice, freshIds = false) => {
      if (inv.lineItems && inv.lineItems.length > 0) {
        setLineItems(inv.lineItems.map((li) => ({
          id: freshIds ? genId() : ((li as any).id || genId()),
          description: li.description || '',
          quantity: String(li.quantity || 1),
          unitAmount: String(li.unitAmount || 0),
        })));
      } else {
        setLineItems([{ id: genId(), description: '', quantity: '1', unitAmount: '' }]);
      }
    };

    if (editInvoice) {
      setInvoiceDate(editInvoice.date || today());
      setDueDate(editInvoice.dueDate || defaultDueDate());
      setReference(editInvoice.reference || '');
      setLineAmountTypes(editInvoice.lineAmountTypes || 'Exclusive');
      setStatus(
        (editInvoice.status === 'DRAFT' || editInvoice.status === 'APPROVED')
          ? editInvoice.status
          : 'APPROVED'
      );
      if (editInvoice.lineItems && editInvoice.lineItems.length > 0) {
        populateLineItems(editInvoice);
      } else {
        setLoadingLineItems(true);
        fetchInvoiceDetail(editInvoice.id)
          .then((full) => { if (full) populateLineItems(full); })
          .catch(() => { /* already logged by context */ })
          .finally(() => setLoadingLineItems(false));
      }
    } else if (templateInvoice) {
      setInvoiceDate(today());
      setDueDate(defaultDueDate());
      setReference(templateInvoice.reference || '');
      setLineAmountTypes(templateInvoice.lineAmountTypes || 'Exclusive');
      setStatus('APPROVED');
      setAccountCode('200');
      setSelectedCustomer(
        templateInvoice.contactId
          ? {
              id: templateInvoice.contactId,
              name: templateInvoice.contactName || '',
              email: templateInvoice.contactEmail ?? undefined,
            }
          : null
      );
      if (templateInvoice.lineItems && templateInvoice.lineItems.length > 0) {
        populateLineItems(templateInvoice, true);
      } else {
        setLoadingLineItems(true);
        fetchInvoiceDetail(templateInvoice.id)
          .then((full) => { if (full) populateLineItems(full, true); })
          .catch(() => { /* already logged */ })
          .finally(() => setLoadingLineItems(false));
      }
    } else {
      setSelectedCustomer(preSelectedCustomer ?? null);
      setInvoiceDate(today());
      setDueDate(defaultDueDate());
      setReference('');
      setAccountCode('200');
      setLineAmountTypes('Exclusive');
      setStatus('APPROVED');
      setLineItems([{ id: genId(), description: '', quantity: '1', unitAmount: '' }]);
    }
  }, [show, editInvoice, templateInvoice, fetchInvoiceDetail, preSelectedCustomer]);

  // ── Line item handlers ───────────────────────────────────────────────
  const addLineItem = () => {
    setLineItems((prev) => [
      ...prev,
      { id: genId(), description: '', quantity: '1', unitAmount: '' },
    ]);
  };

  const removeLineItem = (id: string) => {
    setLineItems((prev) => prev.filter((item) => item.id !== id));
  };

  const updateLineItem = (id: string, field: keyof LineItemRow, value: string) => {
    setLineItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, [field]: value } : item))
    );
  };

  // ── Live totals ──────────────────────────────────────────────────────
  const subtotal = lineItems.reduce((sum, item) => {
    const qty = parseFloat(item.quantity) || 0;
    const unit = parseFloat(item.unitAmount) || 0;
    return sum + qty * unit;
  }, 0);

  const gstAmount =
    lineAmountTypes === 'NoTax' ? 0
    : lineAmountTypes === 'Inclusive' ? Math.round((subtotal / 11) * 100) / 100
    : Math.round((subtotal * 0.1) * 100) / 100;

  const invoiceTotal =
    lineAmountTypes === 'Inclusive' ? subtotal
    : lineAmountTypes === 'NoTax' ? subtotal
    : subtotal + gstAmount;

  // ── Validation ───────────────────────────────────────────────────────
  const validate = (): string | null => {
    if (!isEditMode && !selectedCustomer) return 'select a customer';
    if (lineItems.length === 0)           return 'at least one line item is required';
    for (let i = 0; i < lineItems.length; i++) {
      const item = lineItems[i];
      if (!item.description.trim())       return `line ${i + 1}: description required`;
      const qty = parseFloat(item.quantity);
      if (isNaN(qty) || qty <= 0)         return `line ${i + 1}: invalid quantity`;
      const unit = parseFloat(item.unitAmount);
      if (isNaN(unit))                    return `line ${i + 1}: invalid unit amount`;
    }
    return null;
  };

  // ── Submit ───────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setError(null);
    setSuccess(null);
    const err = validate();
    if (err) { setError(err); return; }

    try {
      if (isEditMode && editInvoice) {
        const result = await updateInvoice({
          invoiceId: editInvoice.id,
          date: invoiceDate,
          dueDate,
          reference: reference.trim() || undefined,
          status,
          lineAmountTypes,
          lineItems: lineItems.map((item) => ({
            description: item.description.trim(),
            quantity: parseFloat(item.quantity),
            unitAmount: parseFloat(item.unitAmount),
            taxType: lineAmountTypes === 'NoTax' ? 'NONE' as const : 'GST' as const,
            accountCode: accountCode.trim() || undefined,
          })),
        });
        setSuccess(`updated ${result.invoice?.invoiceNumber || editInvoice.invoiceNumber}`);
        setTimeout(() => onHide(), 1500);
      } else {
        const result = await createInvoice({
          contactId: selectedCustomer!.id,
          contactName: selectedCustomer!.name,
          contactEmail: selectedCustomer!.email,
          reference: reference.trim() || undefined,
          date: invoiceDate,
          dueDate,
          status,
          lineAmountTypes,
          lineItems: lineItems.map((item) => ({
            description: item.description.trim(),
            quantity: parseFloat(item.quantity),
            unitAmount: parseFloat(item.unitAmount),
            taxType: lineAmountTypes === 'NoTax' ? 'NONE' as const : 'GST' as const,
            accountCode: accountCode.trim() || undefined,
          })),
        });
        setSuccess(`created ${result.invoice?.invoiceNumber}`);
        setTimeout(() => onHide(), 1500);
      }
    } catch (e: any) {
      setError(e?.message || `failed to ${isEditMode ? 'update' : 'create'} invoice`);
    }
  };

  const isLoading = isEditMode ? updateInvoiceLoading : createInvoiceLoading;

  // ── Header label ─────────────────────────────────────────────────────
  const headLabel = isEditMode
    ? `edit-invoice ${editInvoice?.invoiceNumber || ''}`
    : isCopyMode
      ? `copy-invoice ${templateInvoice?.invoiceNumber || ''}`
      : 'create-invoice';

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      fullscreen="md-down"
      dialogClassName="term-invoice-form-dialog"
      contentClassName="term-invoice-form-content"
    >
      <div className="term-invoice-form">
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="term-invoice-form__head">
          <div className="term-invoice-form__title">
            <span className="term-invoice-form__prompt">$</span>
            <span className="term-invoice-form__label">{headLabel}</span>
          </div>
          <button
            type="button"
            className="term-invoice-form__close"
            onClick={onHide}
            aria-label="Close"
            disabled={isLoading}
          >
            [×]
          </button>
        </div>

        {/* ── Status banners ─────────────────────────────────────────── */}
        {error && (
          <div className="term-invoice-form__banner term-invoice-form__banner--error">
            <span className="term-invoice-form__banner-glyph">✗</span>
            <span>{error}</span>
          </div>
        )}
        {success && (
          <div className="term-invoice-form__banner term-invoice-form__banner--success">
            <span className="term-invoice-form__banner-glyph">✓</span>
            <span>{success}</span>
          </div>
        )}
        {isEditMode && editInvoice?.status === 'PAID' && (
          <div className="term-invoice-form__banner term-invoice-form__banner--warn">
            <span className="term-invoice-form__banner-glyph">!</span>
            <span>this invoice is paid — some fields may not be editable</span>
          </div>
        )}

        {/* ── Customer ───────────────────────────────────────────────── */}
        <div className="term-invoice-form__field">
          <label className="term-invoice-form__field-label">customer</label>
          {isEditMode ? (
            <input
              className="term-invoice-form__input term-invoice-form__input--locked"
              type="text"
              value={editInvoice?.contactName || '(unknown)'}
              disabled
            />
          ) : (
            <select
              className="term-invoice-form__input"
              value={selectedCustomer?.id || ''}
              onChange={(e) => {
                const id = e.target.value;
                const c = customers.find((x: any) => x.id === id);
                setSelectedCustomer(
                  c && c.id
                    ? { id: c.id, name: c.name || c.company || '', email: c.email }
                    : null
                );
              }}
            >
              <option value="">(select a customer)</option>
              {customers.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.company || c.id}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* ── Dates ──────────────────────────────────────────────────── */}
        <div className="term-invoice-form__row">
          <div className="term-invoice-form__field">
            <label className="term-invoice-form__field-label">invoice date</label>
            <input
              className="term-invoice-form__input"
              type="date"
              value={invoiceDate}
              onChange={(e) => setInvoiceDate(e.target.value)}
            />
          </div>
          <div className="term-invoice-form__field">
            <label className="term-invoice-form__field-label">due date</label>
            <input
              className="term-invoice-form__input"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
            />
          </div>
        </div>

        {/* ── Reference ──────────────────────────────────────────────── */}
        <div className="term-invoice-form__field">
          <label className="term-invoice-form__field-label">reference (optional)</label>
          <input
            className="term-invoice-form__input"
            type="text"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            placeholder="e.g. PO number, job reference"
          />
        </div>

        {/* ── GST / status / account-code three-up ───────────────────── */}
        <div className="term-invoice-form__row term-invoice-form__row--three">
          <div className="term-invoice-form__field">
            <label className="term-invoice-form__field-label">gst treatment</label>
            <select
              className="term-invoice-form__input"
              value={lineAmountTypes}
              onChange={(e) => setLineAmountTypes(e.target.value as LineAmountType)}
            >
              <option value="Exclusive">exclusive (ex GST)</option>
              <option value="Inclusive">inclusive (inc GST)</option>
              <option value="NoTax">no tax</option>
            </select>
          </div>
          <div className="term-invoice-form__field">
            <label className="term-invoice-form__field-label">status</label>
            <select
              className="term-invoice-form__input"
              value={status}
              onChange={(e) => setStatus(e.target.value as 'DRAFT' | 'APPROVED')}
              disabled={
                isEditMode
                && editInvoice?.status !== 'DRAFT'
                && editInvoice?.status !== 'APPROVED'
              }
            >
              <option value="APPROVED">awaiting payment</option>
              <option value="DRAFT">draft</option>
            </select>
          </div>
          <div className="term-invoice-form__field">
            <label className="term-invoice-form__field-label">account code</label>
            <input
              className="term-invoice-form__input"
              type="text"
              value={accountCode}
              onChange={(e) => setAccountCode(e.target.value)}
              placeholder="200"
            />
          </div>
        </div>

        {/* ── Line items ─────────────────────────────────────────────── */}
        <div className="term-invoice-form__section">
          <div className="term-invoice-form__section-head">
            <span className="term-invoice-form__section-rule">── line items ──</span>
            {loadingLineItems && (
              <span className="term-invoice-form__section-hint">
                · loading line items…
              </span>
            )}
          </div>

          <div className="term-invoice-form__lines">
            <div className="term-invoice-form__lines-row term-invoice-form__lines-row--head">
              <span className="term-invoice-form__lines-desc">description</span>
              <span className="term-invoice-form__lines-qty">qty</span>
              <span className="term-invoice-form__lines-unit">unit price</span>
              <span className="term-invoice-form__lines-amt">amount</span>
              <span className="term-invoice-form__lines-rm" aria-hidden="true" />
            </div>

            {lineItems.map((item, idx) => {
              const qty = parseFloat(item.quantity) || 0;
              const unit = parseFloat(item.unitAmount) || 0;
              const amount = qty * unit;
              return (
                <div key={item.id} className="term-invoice-form__lines-row">
                  <input
                    className="term-invoice-form__input term-invoice-form__input--line"
                    type="text"
                    value={item.description}
                    onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                    placeholder="description"
                    aria-label={`line ${idx + 1} description`}
                  />
                  <input
                    className="term-invoice-form__input term-invoice-form__input--line term-invoice-form__input--num"
                    type="number"
                    min="0"
                    step="0.01"
                    value={item.quantity}
                    onChange={(e) => updateLineItem(item.id, 'quantity', e.target.value)}
                    aria-label={`line ${idx + 1} quantity`}
                  />
                  <input
                    className="term-invoice-form__input term-invoice-form__input--line term-invoice-form__input--num"
                    type="number"
                    step="0.01"
                    value={item.unitAmount}
                    onChange={(e) => updateLineItem(item.id, 'unitAmount', e.target.value)}
                    placeholder="0.00"
                    aria-label={`line ${idx + 1} unit price`}
                  />
                  <span className="term-invoice-form__lines-amt-val">{fmtCurrency(amount)}</span>
                  <span className="term-invoice-form__lines-rm">
                    {lineItems.length > 1 && (
                      <button
                        type="button"
                        className="term-invoice-form__row-rm"
                        onClick={() => removeLineItem(item.id)}
                        aria-label={`remove line ${idx + 1}`}
                        title="Remove line"
                      >
                        [×]
                      </button>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            className="term-invoice-form__action"
            onClick={addLineItem}
          >
            [+ add line]
          </button>

          {/* ── Totals panel ───────────────────────────────────────── */}
          <div className="term-invoice-form__totals">
            <div className="term-invoice-form__totals-row">
              <span className="term-invoice-form__totals-key">
                subtotal
                {lineAmountTypes === 'Inclusive' ? ' (inc GST)'
                  : lineAmountTypes === 'NoTax' ? ''
                  : ' (ex GST)'}
              </span>
              <span className="term-invoice-form__totals-val">{fmtCurrency(subtotal)}</span>
            </div>
            {lineAmountTypes !== 'NoTax' && (
              <div className="term-invoice-form__totals-row term-invoice-form__totals-row--dim">
                <span className="term-invoice-form__totals-key">
                  {lineAmountTypes === 'Inclusive' ? 'includes GST of' : 'GST (10%)'}
                </span>
                <span className="term-invoice-form__totals-val">{fmtCurrency(gstAmount)}</span>
              </div>
            )}
            <div className="term-invoice-form__totals-row term-invoice-form__totals-row--total">
              <span className="term-invoice-form__totals-key">total</span>
              <span className="term-invoice-form__totals-val">{fmtCurrency(invoiceTotal)}</span>
            </div>
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────── */}
        <div className="term-invoice-form__footer">
          <button
            type="button"
            className="term-invoice-form__action term-invoice-form__action--dim"
            onClick={onHide}
            disabled={isLoading}
          >
            [cancel]
          </button>
          <span className="term-invoice-form__footer-spacer" />
          <button
            type="button"
            className="term-invoice-form__action term-invoice-form__action--accent"
            onClick={handleSubmit}
            disabled={isLoading || loadingLineItems}
          >
            {isLoading
              ? (isEditMode ? '[updating…]' : '[creating…]')
              : (isEditMode ? '[update invoice]' : '[create invoice]')}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default TerminalCreateInvoiceModal;
